import React, { useEffect, useMemo, useState } from "react";
import uuid from "react-uuid";
import { useNavigate, useOutletContext } from "react-router";
import { useMediaQuery } from "react-responsive";
import axios from "axios";
import { useSelector } from "react-redux";

import { FaChevronLeft } from "react-icons/fa6";

import SectionHeader from "../common/sectionTitle";
import ProductList from "./cart/productList";
import OrderSummary from "./cart/orderSummary";
import EditOptionModal from "./cart/editOptionModal";

import { CartContainer } from "./styled";

const Cart = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ query: "(max-width:767px)" });

  const user = useSelector((state) => state?.user?.data[0]);

  const [data, setData] = useState([]);
  const [selection, setSelection] = useState(null);

  const { openModal, closeModal } = useOutletContext();

  useEffect(() => {
    if (user) {
      getCart();
    }
  }, [user]);

  const groupCartByCompany = (cartList) => {
    const grouped = {};

    cartList.forEach((item) => {
      const { cmpnyNo, cmpnyNm, ...rest } = item;

      if (!grouped[cmpnyNo]) {
        grouped[cmpnyNo] = {
          cmpnyNm,
          cmpnyNo,
          products: [],
        };
      }

      grouped[cmpnyNo].products.push({
        ...rest,
      });
    });

    return Object.values(grouped);
  };

  const getCart = async () => {
    const url = "/erp/cart/get";
    const body = { mberNo: user.mberNo, platform: "MULTI_PAGE" };

    try {
      const res = await axios.post(url, body);
      if (res.status === 200) {
        const groupedData = groupCartByCompany(res.data);
        setData(groupedData);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const updateCart = (updatedItem) => {
    setData((prev) =>
      prev.map((group) => ({
        ...group,
        products: group.products.map((product) =>
          product.id === updatedItem.id ? { ...product, ...updatedItem } : product,
        ),
      })),
    );
  };

  const selectionDelete = (isAll = false) => {
    const allItems = data.flatMap((company) => company.products);
    const targetItems = isAll ? allItems : allItems.filter((item) => item.checked);

    const selectedIds = targetItems.map((item) => item.id);

    if (selectedIds.length === 0) return;

    openModal({
      isOpen: true,
      type: "confirmation",
      title: `${isAll ? "전체" : "선택"} 삭제`,
      message: [
        isAll
          ? "장바구니에 있는 상품을 삭제하시겠습니까?"
          : `선택하신 상품 ${selectedIds.length}개를 삭제하시겠습니까?`,
      ],
      handleConfirm: () => deleteProduct(selectedIds),
      handleCancel: closeModal,
    });
  };

  const deleteProduct = async (ids) => {
    const url = "/erp/cart/delete";
    const body = { cartIds: ids };

    try {
      const res = await axios.post(url, body);
      if (res.status === 200) {
        setData((prev) =>
          prev
            .map((group) => ({
              ...group,
              products: group.products.filter((product) => !ids.includes(product.id)),
            }))
            .filter((group) => group.products.length > 0),
        );
      }
    } catch (error) {
      console.log(error);
    } finally {
      closeModal();
    }
  };

  const totalProductCount = useMemo(
    () => data.reduce((sum, group) => sum + group.products.length, 0),
    [data],
  );

  return (
    <CartContainer>
      {selection && (
        <EditOptionModal selection={selection} setSelection={setSelection} getCart={getCart} />
      )}
      <h2>
        {isMobile && (
          <button onClick={() => navigate(`/shop/mypage`)}>
            <FaChevronLeft />
          </button>
        )}
        <span>장바구니</span>
      </h2>
      <SectionHeader title={`총 ${totalProductCount || 0}개`} withLine />
      <ProductList
        data={data}
        openModal={openModal}
        closeModal={closeModal}
        selectionDelete={selectionDelete}
        deleteProduct={deleteProduct}
        totalProductCount={totalProductCount}
        setSelection={setSelection}
        updateCart={updateCart}
        setData={setData}
      />
      <OrderSummary data={data} selectionDelete={selectionDelete} />
    </CartContainer>
  );
};

export default Cart;
